/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useEffect, useState} from 'react';
import {Button, Table, TableColumnsType} from "antd";
import {DeleteOutlined, EditOutlined, MessageOutlined} from "@ant-design/icons";
import {useStore} from "zustand/index";
import styles from './Notice.module.scss';
import PageTitle from "../../components/common/PageTitle/PageTitle";
import {NoticeType} from "../../types/NoticeType";
import noticeAPI from "../../api/noticeAPI";
import {formatKorDate} from "../../util/date";
import WriteNoticePopup from "./popup/WriteNoticePopup";
import EditNoticePopup from "./popup/EditNoticePopup";
import DeleteNoticePopup from "./popup/DeleteNoticePopup";
import apiClient from "../../api/apiClient";
import {useMessageStore} from "../../stores/messageStore";
import {useAppStore} from "../../stores/appStore";

const emptyPopupData = {
	id: '',
	title: '',
	content: '',
};

const Notice = () => {
	const { setMessages } = useStore(useMessageStore);
	const setShouldShowSpinner = useAppStore((state) => state.setShouldShowSpinner);
	const [list, setList] = useState<NoticeType[]>([]);
	const [isOpenWritePopup, setIsOpenWritePopup] = useState(false);
	const [isOpenEditPopup, setIsOpenEditPopup] = useState(false);
	const [isOpenDeletePopup, setIsOpenDeletePopup] = useState(false);
	const [popupData, setPopupData] = useState<Omit<NoticeType, 'createDate'>>(emptyPopupData);

	useEffect(() => {
		noticeAPI.fetchNoticeAPI()
			.then((res) => setList(res));
	}, []);

	const onSubmitWrite = async (title: string, content: string) => {
		try {
			setShouldShowSpinner(true);
			await noticeAPI.writeNoticeAPI(title, content);
			const newList = await noticeAPI.fetchNoticeAPI();
			setList(newList);
			setIsOpenWritePopup(false);
		} catch (e) {
			console.error(e);
		} finally {
			setShouldShowSpinner(false);
		}

	};

	const onSubmitEdit = async (id: string, title: string, content: string) => {
		try {
			setShouldShowSpinner(true);
			await noticeAPI.editNoticeAPI(id, title, content);
			const newList = await noticeAPI.fetchNoticeAPI();
			setList(newList);
			setIsOpenEditPopup(false);
		} catch (e) {
			console.error(e);
		} finally {
			setShouldShowSpinner(false);
		}
	};

	const onDelete = async (id: string) => {
		try {
			setShouldShowSpinner(true);
			await noticeAPI.deleteNoticeAPI(id);
			const newList = await noticeAPI.fetchNoticeAPI();
			setList(newList);
			setIsOpenDeletePopup(false);
		} catch (e) {
			console.error(e);
		} finally {
			setShouldShowSpinner(false);
		}
	};

	const sendFCM = async (record: NoticeType) => {
		const { title, content, id } = record;

		try {
			setShouldShowSpinner(true);
			await apiClient.post(`/api/admin/business/notice`, {
				notificationKey: 'system_notice',
				tplParams: {
					title,
					body: content,
				},
				data: {
					id,
				},
			});
			setMessages('success', `메시지 전송 성공: ${title}`);
		} catch (e) {
			console.error(e);
			setMessages('error', '메시지 전송 실패');
		} finally {
			setShouldShowSpinner(false);
		}
	};

	const columns: TableColumnsType<NoticeType> = [
		{
			title: '작성일',
			dataIndex: 'createDate',
			key: 'createDate',
			width: 100,
			align: 'center',
			render: (text: string) => <div>{formatKorDate(text)}</div>,
		},
		{
			title: '제목',
			dataIndex: 'title',
			key: 'title',
			width: 150,
			align: 'center',
		},
		{
			title: '내용',
			dataIndex: 'content',
			key: 'content',
			width: 250,
			align: 'center',
		},
		{
			title: 'FCM 전송',
			dataIndex: '',
			key: '',
			width: 30,
			align: 'center',
			render: (value: any, record: NoticeType, index: number) => (
				<Button
					shape='circle'
					onClick={async (e) => {
						e.stopPropagation();
						await sendFCM(record);
					}}
				>
					<MessageOutlined />
				</Button>
			)
		},
		{
			title: '수정',
			dataIndex: 'update',
			key: 'update',
			width: 30,
			align: 'center',
			render: (value: any, record: NoticeType, index: number) => (
				<Button
					type="primary"
					shape='circle'
					onClick={(e) => {
						e.stopPropagation();
						setIsOpenEditPopup(true);
						setPopupData({ id: record.id, title: record.title, content: record.content });
					}}
				>
					<EditOutlined />
				</Button>
			)
		},
		{
			title: '삭제',
			dataIndex: 'delete',
			key: 'delete',
			width: 30,
			align: 'center',
			render: (value: any, record: NoticeType, index: number) => (
				<Button
					shape='circle'
					type='primary'
	        danger
					onClick={(e) => {
						e.stopPropagation();
						setIsOpenDeletePopup(true);
						setPopupData({ id: record.id, title: record.title, content: record.content });
					}}
				>
					<DeleteOutlined />
				</Button>
			),
		},
	];

	return (
		<div className={styles.wrap}>
			<div className={styles.topWrap}>
				<PageTitle title="공지사항"/>
				<Button type="primary" onClick={() => setIsOpenWritePopup(prev => !prev)}>
					작성하기
				</Button>
			</div>

			<Table
				bordered
				pagination={false}
				columns={columns}
				dataSource={list.map((row) => ({...row, key: row.id}))}
			/>

			{/* 신규 작성 팝업 */}
			{isOpenWritePopup && (
				<WriteNoticePopup
					showPopup={isOpenWritePopup}
					onCancel={() => setIsOpenWritePopup(false)}
					onSubmit={onSubmitWrite}
				/>
			)}

			{/* 수정 팝업 */}
			{isOpenEditPopup && (
				<EditNoticePopup
					popupData={popupData}
					showPopup={isOpenEditPopup}
					onCancel={() => {
						setIsOpenEditPopup(false);
						setPopupData(emptyPopupData);
					}}
					onSubmit={onSubmitEdit}
				/>
			)}

			{/* 삭제 팝업 */}
			{isOpenDeletePopup && (
				<DeleteNoticePopup
					popupData={popupData}
					showPopup={isOpenDeletePopup}
					onCancel={() => {
						setIsOpenDeletePopup(false);
						setPopupData(emptyPopupData);
					}}
					onDelete={onDelete}
				/>
			)}
		</div>
	);
};

export default Notice;
